import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { pathOf, withReturnTo } from "@/lib/returnTo";
import { isWrongRecipient, readRecipient } from "@/lib/emailRecipient";
import WrongAccountNotice from "@/components/auth/WrongAccountNotice";
import FixyBeeMark from "@/components/brand/FixyBeeMark";

export default function ProtectedRoute({
  children,
  requireRole = null,
  requireAdmin = false,
  requireFeature = null,
  requireTier = null,
}) {
  const { user, features, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream gap-4" data-testid="protected-loading">
        <div className="animate-bee-wings"><FixyBeeMark size={56} /></div>
        <p className="font-display text-smoke text-sm">{t("common.loading")}</p>
      </div>
    );
  }

  if (!user) {
    // `?returnTo=`, not `state.from`. The state was set here and read by
    // nobody, so every deep link that hit this line ended on a dashboard —
    // the same silent drop the signup flow had. One mechanism now, and it is
    // the one Login already reads.
    return <Navigate to={withReturnTo("/login", pathOf(location))} replace />;
  }

  // R11: an email deep link names the account it was sent to, and this is the
  // only place every one of them passes through.
  //
  // **Checked before the role redirect below, and that order is the fix.** On
  // 17-sep the closing reminder for a job was opened with another account's
  // session live in the browser; the role gate answered first, sent the visitor
  // to that account's own dashboard, and the redirect is what destroyed the
  // evidence — by the time anything was on screen, the URL that said who the
  // link was for was gone. Answering here keeps the mismatch nameable.
  //
  // It runs after `!user` deliberately. A logged-out click is not a wrong
  // account, it is no account: the login redirect carries the whole path,
  // `for=` included, and this check gets its turn when they come back.
  const recipient = readRecipient(location.search);
  if (isWrongRecipient(recipient, user)) {
    return <WrongAccountNotice hint={recipient.hint} target={pathOf(location)} />;
  }

  const role = user?.profile?.role;
  const isAdmin = user?.profile?.is_admin;

  if (!role) {
    // Only an account from before 2026-10-28 can be here: sign-up writes the
    // role now. It finishes on the sign-up page of the side the address it was
    // going to belongs to — `/provider/*` is a FixyBee's, anything else
    // (`/jobs/new`, `/client/*`, `/panel`) a client's — and that page's footer
    // links to the other one. No "which are you?" screen in between.
    const door = location.pathname.startsWith("/provider/") ? "/signup/provider" : "/signup/client";
    return <Navigate to={withReturnTo(door, pathOf(location))} replace />;
  }

  // **Not a silent bounce to the index** (T18-M6). This used to be
  // `<Navigate to="/" replace />`, and that is the whole of what Jorge and
  // Daniel saw: the "Open the queue" button in a new-ticket email, pressed in a
  // browser holding a non-admin session, landed on the home page with nothing
  // on screen saying why. The only available reading was that the link was
  // broken.
  //
  // It is the same defect R11 already fixed one branch below, arriving through
  // a door that branch does not watch: `readRecipient` needs a `for=` hint in
  // the URL, and an email addressed to the support mailbox carries no personal
  // recipient to name. What it has instead is a requirement — be an admin —
  // and a session that does not meet it, which is the same sentence.
  //
  // So the same answer: say the link was for another account and offer the one
  // action that fixes it. `WrongAccountNotice` carries this exact path into
  // `returnTo`, so signing in as the admin lands on the queue rather than on a
  // dashboard. No `hint`: there is no recipient address to mask, and the copy
  // already has a no-hint wording for precisely that case.
  if (requireAdmin && !isAdmin) {
    return <WrongAccountNotice target={pathOf(location)} />;
  }

  if (requireRole && role !== requireRole && !isAdmin) {
    // T25: a client who became a FixyBee still holds links to `/client/*` —
    // emails, pushes, the bell — for jobs that are still theirs. Each has a
    // twin on the FixyBee side; send them there rather than to a dashboard
    // that has lost what they were opening.
    if (role === "provider" && requireRole === "client") {
      return <Navigate to={providerTwin(location.pathname) + location.search} replace />;
    }
    // Redirect users to their proper dashboard
    if (role === "client") return <Navigate to="/client/dashboard" replace />;
    if (role === "provider") return <Navigate to="/provider/dashboard" replace />;
    return <Navigate to="/" replace />;
  }

  // Feature flag and tier gates. Unlike requireRole, admins do NOT bypass these:
  // the flag means the feature does not exist for this deployment, and the
  // backend rejects the same calls, so letting an admin through would only
  // render a page whose every request 403s.
  const ownDashboard = role === "provider" ? "/provider/dashboard" : "/client/dashboard";
  if (requireFeature && !features?.[requireFeature]) {
    return <Navigate to={ownDashboard} replace />;
  }
  if (requireTier !== null && (user?.profile?.tier_priority || 0) < requireTier) {
    return <Navigate to={ownDashboard} replace />;
  }

  return children;
}

/** The FixyBee-side address of a `/client/*` page (DY2.6 routes, T25). */
export function providerTwin(pathname) {
  const path = pathname.replace(/\/+$/, "");
  if (path === "/client/jobs") return "/provider/requests";
  if (path === "/client/jobs/new") return "/provider/requests/new";
  const job = path.match(/^\/client\/jobs\/([^/]+)(\/edit)?$/);
  if (job) return `/provider/jobs/${job[1]}${job[2] || ""}`;
  if (path === "/client/messages") return "/provider/messages";
  if (path === "/client/settings") return "/provider/settings";
  return "/provider/dashboard";
}
